import type { FC } from 'react';

import type { CampusListItem } from '@/types/campus.types';

interface DeleteCampusModalProps {
  campus: CampusListItem;
  isDeleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const DeleteCampusModal: FC<DeleteCampusModalProps> = ({ campus, isDeleting, onConfirm, onClose }) => {
  const hasUsers = campus.userCount > 0;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-xl">
        <div className="px-6 py-5">
          <h2 className="text-[15px] font-bold text-[var(--color-ink)]">Delete Campus</h2>
          <p className="mt-2 text-[13px] text-[var(--color-ink-muted)]">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-[var(--color-ink)]">{campus.name}</span>? This action
            cannot be undone.
          </p>

          {hasUsers ? (
            <div className="mt-3 rounded-xl border border-[var(--color-warn-bg)] bg-[var(--color-warn-bg)] px-4 py-3">
              <p className="text-[12px] font-semibold text-[var(--color-warn-ink)]">
                ⚠ Cannot delete — {campus.userCount} registered user
                {campus.userCount === 1 ? '' : 's'}
              </p>
              <p className="mt-0.5 text-[11px] text-[var(--color-warn-ink)]">
                Reassign or remove all users from this campus before deleting it.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-[var(--color-divider)] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-4 py-2 text-[13px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting || hasUsers}
            className="rounded-lg bg-[var(--color-danger-ink)] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </>
  );
};

export default DeleteCampusModal;
